import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  apiErrorText,
  useAckContractHandoffMutation,
  useGetContractBriefQuery,
  useImportContractVersionEstimateMutation,
  useListStatutProjectsQuery,
  useSetContractProjectMutation,
  useTriggerExecutionTermMutation,
} from '@shared/api';
import { useAppSelector } from '@app/hooks';
import { useAgentPermission } from '@entities/user';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { documentKindLabel } from '@shared/lib/documentKind';
import { setActiveContractId, setActiveProjectId } from '@shared/lib/activeProjectContext';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';

const Wrap = styled.div`
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const BackLink = styled(Link)`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const SubjectText = styled.div`
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Grid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const KvItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const KvLabel = styled.span`
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.muted};
  font-weight: 600;
`;

const KvValue = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`;

const SectionTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 16px;
  font-weight: 700;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const Muted = styled.div`
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SmallMuted = styled.div`
  font-size: 12px;
  margin-top: 2px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const DutyItem = styled.div`
  font-size: 12.5px;
  margin-bottom: 2px;
`;

const AckStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ErrText = styled.div`
  margin-bottom: 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU');
}

function fmtMoney(sum: number | null | undefined, currency: string | null | undefined): string {
  if (sum == null) return '—';
  return `${sum.toLocaleString('ru-RU')} ${currency ?? 'RUB'}`;
}

/**
 * Безопасный паспорт (сводка) договора для смежных служб (`/contracts/:id/brief`).
 * Доступен любому авторизованному сотруднику (ПТО, МТО, прорабу, геодезисту, механику),
 * но сумма договора и условия оплаты раскрываются ТОЛЬКО при наличии прав на
 * `ОСНОВА.Юрист` или `ОСНОВА.Экономист`.
 */
export function ContractBriefView() {
  const { id } = useParams<{ id: string }>();
  const contractId = id ?? '';
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const { data, isLoading, isError } = useGetContractBriefQuery(contractId, {
    skip: !contractId,
  });
  const { can_read: canOpenStatut, can_write: canWriteStatut } = useAgentPermission('statut');
  const { can_write: canWriteSmeta } = useAgentPermission('smeta');
  const { data: projectsData } = useListStatutProjectsQuery();
  const [setContractProject, { isLoading: savingProject }] = useSetContractProjectMutation();
  const [triggerTerm, { isLoading: triggeringTerm }] = useTriggerExecutionTermMutation();
  const [ack, ackState] = useAckContractHandoffMutation();
  const [importVersionEst, { isLoading: importingEst }] =
    useImportContractVersionEstimateMutation();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [ackErr, setAckErr] = useState<string | null>(null);
  const [estMsg, setEstMsg] = useState<string | null>(null);
  const [termEventDates, setTermEventDates] = useState<Record<string, string>>({});
  const [termMsg, setTermMsg] = useState<string | null>(null);

  useEffect(() => {
    if (contractId) {
      setActiveContractId(contractId);
    }
    const pid = (data?.contract as { project_id?: string | null } | undefined)?.project_id;
    if (pid) {
      setActiveProjectId(pid);
    }
  }, [contractId, data]);

  if (isLoading) return <Muted>Загрузка паспорта договора…</Muted>;
  if (isError || !data) return <Muted>Договор не найден или нет доступа.</Muted>;

  const c = data.contract;
  const contractProjectId = (c as { project_id?: string | null }).project_id ?? '';
  const statutProjects = projectsData?.items ?? [];
  const execution_terms = data.execution_terms ?? [];
  const handoffs = data.handoffs ?? [];
  const deadlines = data.deadlines ?? [];
  const attachments = data.attachments ?? [];
  const penalties = data.penalties ?? [];

  async function onChangeProject(projectId: string) {
    setAckErr(null);
    try {
      await setContractProject({
        contractId: c.id,
        project_id: projectId || null,
      }).unwrap();
      if (projectId) {
        setActiveProjectId(projectId);
      }
    } catch (e) {
      setAckErr(apiErrorText(e, 'Не удалось сохранить привязку к строительному объекту.'));
    }
  }

  async function onTriggerTerm(termId: string) {
    setAckErr(null);
    setTermMsg(null);
    try {
      const eventDate = termEventDates[termId]?.trim() || undefined;
      const res = await triggerTerm({
        contractId: c.id,
        termId,
        event_date: eventDate,
      }).unwrap();
      setTermMsg(`Запущен отсчёт срока по условию (дедлайн: ${fmtDate(res.due_at)}).`);
    } catch (e) {
      setAckErr(apiErrorText(e, 'Не удалось активировать условное обязательство.'));
    }
  }

  async function onAck(handoffId: string) {
    setAckErr(null);
    try {
      await ack({
        id: handoffId,
        contractId: c.id,
        note: notes[handoffId]?.trim() || undefined,
      }).unwrap();
    } catch (e) {
      const msg = (e as { data?: { error?: { message?: string } } })?.data?.error?.message;
      setAckErr(msg || 'Отметить принятие может только сотрудник соответствующей службы.');
    }
  }

  async function onDownloadAttachment(fileId: string, filename: string) {
    setAckErr(null);
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
    const res = await downloadAuthorizedFile(
      `${base}/agents/statut/contracts/${c.id}/brief/files/${fileId}`,
      { accessToken, fallbackName: filename },
    );
    if (!res.ok) {
      setAckErr(`Не удалось скачать приложение (${res.error}).`);
    }
  }

  async function onImportToSmeta(fileId: string) {
    setAckErr(null);
    setEstMsg(null);
    try {
      const res = await importVersionEst({
        contractId: c.id,
        file_id: fileId,
        target: 'both',
      }).unwrap();
      setEstMsg(
        `Сметное приложение «${res.filename}» передано в эталон Сметчика (ЛСР: ${res.imported_lsr_count}, позиций: ${res.imported_lsr_items || res.estimate_items_count}).`,
      );
    } catch (e) {
      setAckErr(apiErrorText(e, 'Не удалось импортировать приложение в эталон Сметчика.'));
    }
  }

  return (
    <Wrap>
      <TopBar>
        <BackLink to="/">← На главную</BackLink>
        {canOpenStatut && (
          <Link to={`/agents/statut/contracts/${c.id}`}>
            <Button $variant="secondary" $size="sm">
              Открыть полную карточку в ОСНОВА.Юрист →
            </Button>
          </Link>
        )}
      </TopBar>

      <Card>
        <TitleRow>
          <Title>
            Паспорт договора {c.contract_number ? `№ ${c.contract_number}` : `«${c.title}»`}
          </Title>
          <Badge $tone={c.status === 'signed' ? 'success' : 'info'}>{c.status}</Badge>
        </TitleRow>
        {c.subject && <SubjectText>{c.subject}</SubjectText>}

        <Grid>
          <KvItem>
            <KvLabel>Контрагент</KvLabel>
            <KvValue>
              {c.counterparty}
              {c.counterparty_inn ? ` (ИНН ${c.counterparty_inn})` : ''}
            </KvValue>
          </KvItem>
          <KvItem>
            <KvLabel>Дата подписания</KvLabel>
            <KvValue>{fmtDate(c.signed_at)}</KvValue>
          </KvItem>
          <KvItem>
            <KvLabel>Срок действия</KvLabel>
            <KvValue>{fmtDate(c.expires_at)}</KvValue>
          </KvItem>
          {c.can_see_financials ? (
            <KvItem>
              <KvLabel>Сумма договора</KvLabel>
              <KvValue>{fmtMoney(c.sum, c.currency)}</KvValue>
            </KvItem>
          ) : (
            <KvItem>
              <KvLabel>Финансовый блок</KvLabel>
              <SmallMuted>
                Скрыт (доступен только юридической и экономической службам)
              </SmallMuted>
            </KvItem>
          )}
          <KvItem>
            <KvLabel>Строительный объект (ПТО)</KvLabel>
            <Select
              value={contractProjectId}
              disabled={!canWriteStatut || savingProject}
              onChange={(e) => void onChangeProject(e.target.value)}
              aria-label="Строительный объект договора"
            >
              <option value="">— не привязан —</option>
              {statutProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code ? `${p.code} · ` : ''}
                  {p.name}
                </option>
              ))}
            </Select>
          </KvItem>
        </Grid>
      </Card>

      <Card>
        <SectionTitle>Обязательства и технические условия исполнения</SectionTitle>
        {termMsg && <SmallMuted style={{ marginBottom: 8 }}>{termMsg}</SmallMuted>}
        {execution_terms.length === 0 ? (
          <Muted>Условия исполнения для вашей службы не выделены.</Muted>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Условие / обязательство</th>
                <th>Служба</th>
                <th>Срок / триггер</th>
                <th>Пункт</th>
                <th>Запуск отсчёта</th>
              </tr>
            </thead>
            <tbody>
              {execution_terms.map((t) => (
                <tr key={t.id}>
                  <td>
                    <strong>{t.title}</strong>
                    {t.note && <SmallMuted>{t.note}</SmallMuted>}
                  </td>
                  <td>{t.responsible_role ?? '—'}</td>
                  <td>
                    {t.due_date
                      ? fmtDate(t.due_date)
                      : t.duration_days
                        ? `${t.duration_days} дн. (${t.trigger_event ?? 'по событию'})`
                        : t.trigger_event ?? '—'}
                  </td>
                  <td>{t.clause ?? '—'}</td>
                  <td>
                    {!t.due_date && (t.duration_days || t.trigger_event) ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Input
                          type="date"
                          value={termEventDates[t.id] ?? ''}
                          onChange={(e) =>
                            setTermEventDates((prev) => ({ ...prev, [t.id]: e.target.value }))
                          }
                          style={{ width: 135 }}
                          aria-label="Дата наступления события"
                        />
                        <Button
                          type="button"
                          $variant="secondary"
                          $size="sm"
                          disabled={triggeringTerm}
                          onClick={() => void onTriggerTerm(t.id)}
                        >
                          Запустить срок
                        </Button>
                      </div>
                    ) : t.due_date ? (
                      <Badge $tone="success">На контроле</Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {attachments.length > 0 && (
        <Card>
          <SectionTitle>Приложения к договору (спецификации, графики, ТЗ)</SectionTitle>
          {estMsg && <SmallMuted>{estMsg}</SmallMuted>}
          <Table>
            <thead>
              <tr>
                <th>Файл</th>
                <th>Вид документа</th>
                <th>Добавлен</th>
                <th>Действие</th>
              </tr>
            </thead>
            <tbody>
              {attachments.map((att) => {
                const isEstFile =
                  att.document_kind === 'estimate' ||
                  /\.(xlsx|xls)$/i.test(att.filename ?? '');
                return (
                  <tr key={att.id}>
                    <td>
                      <strong>{att.filename}</strong>
                    </td>
                    <td>{documentKindLabel(att.document_kind)}</td>
                    <td>{fmtDate(att.created_at)}</td>
                    <td>
                      <Button
                        $variant="secondary"
                        $size="sm"
                        onClick={() => void onDownloadAttachment(att.id, att.filename)}
                      >
                        Скачать
                      </Button>
                      {canWriteSmeta && isEstFile && (
                        <>
                          {' '}
                          <Button
                            $variant="secondary"
                            $size="sm"
                            disabled={importingEst}
                            onClick={() => void onImportToSmeta(att.id)}
                          >
                            {importingEst ? 'Импорт…' : 'В эталон Сметчика'}
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      {penalties.length > 0 && (
        <Card>
          <SectionTitle>Штрафные санкции и технические риски</SectionTitle>
          <Table>
            <thead>
              <tr>
                <th>Категория</th>
                <th>Уровень</th>
                <th>Описание условия / санкции</th>
              </tr>
            </thead>
            <tbody>
              {penalties.map((p) => (
                <tr key={p.id}>
                  <td>{p.category}</td>
                  <td>
                    <Badge
                      $tone={
                        p.severity === 'critical'
                          ? 'danger'
                          : p.severity === 'major'
                            ? 'warning'
                            : 'info'
                      }
                    >
                      {p.severity}
                    </Badge>
                  </td>
                  <td>
                    <div>{p.description}</div>
                    {p.clause_quote && <SmallMuted>«{p.clause_quote}»</SmallMuted>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {deadlines.length > 0 && (
        <Card>
          <SectionTitle>Контрольные сроки по договору</SectionTitle>
          <Table>
            <thead>
              <tr>
                <th>Дата</th>
                <th>Вид срока</th>
                <th>Примечание</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {deadlines.map((d) => (
                <tr key={d.id}>
                  <td>
                    <strong>{fmtDate(d.due_at)}</strong>
                  </td>
                  <td>{d.kind}</td>
                  <td>{d.note ?? '—'}</td>
                  <td>
                    <Badge $tone={d.status === 'overdue' ? 'danger' : 'neutral'}>{d.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {handoffs.length > 0 && (
        <Card>
          <SectionTitle>Передача договора в работу службам</SectionTitle>
          {ackErr && <ErrText>{ackErr}</ErrText>}
          <Table>
            <thead>
              <tr>
                <th>Служба</th>
                <th>Поручения по договору</th>
                <th>Статус принятия</th>
              </tr>
            </thead>
            <tbody>
              {handoffs.map((h) => (
                <tr key={h.id}>
                  <td>
                    <strong>{h.service_title}</strong>
                  </td>
                  <td>
                    {h.duties.length === 0 ? (
                      <Muted>Общее ознакомление с договором</Muted>
                    ) : (
                      h.duties.map((d, idx) => (
                        <DutyItem key={`${h.id}-${idx}`}>
                          • {d.title}
                          {d.due_hint ? ` — ${d.due_hint}` : ''}
                          {d.clause ? ` (${d.clause})` : ''}
                        </DutyItem>
                      ))
                    )}
                  </td>
                  <td>
                    {h.acknowledged_at ? (
                      <>
                        <Badge $tone="success">принято в работу</Badge>
                        <SmallMuted>
                          {h.acknowledged_by_name ?? '—'} · {fmtDate(h.acknowledged_at)}
                        </SmallMuted>
                        {h.ack_note && <DutyItem>«{h.ack_note}»</DutyItem>}
                      </>
                    ) : (
                      <AckStack>
                        <Input
                          placeholder="Комментарий при приёмке (опц.)"
                          value={notes[h.id] ?? ''}
                          onChange={(e) =>
                            setNotes((prev) => ({ ...prev, [h.id]: e.target.value }))
                          }
                        />
                        <div>
                          <Button
                            $size="sm"
                            onClick={() => void onAck(h.id)}
                            disabled={ackState.isLoading}
                          >
                            Принял в работу
                          </Button>
                        </div>
                      </AckStack>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </Wrap>
  );
}
